/**
 * Trade Status Badge Component
 *
 * Shared component for displaying trade status with consistent styling.
 * Used in admin trade list and detail pages.
 */

import React from 'react';
import type { AdminTradeStatus } from '../../types/admin.js';

interface TradeStatusBadgeProps {
  status: AdminTradeStatus;
  size?: 'sm' | 'md';
}

const STATUS_CONFIG: Record<AdminTradeStatus, { label: string; className: string }> = {
  proposed: { label: 'Proposed', className: 'bg-blue-100 text-blue-800' },
  countered: { label: 'Countered', className: 'bg-yellow-100 text-yellow-800' },
  accepted: { label: 'Accepted', className: 'bg-cyan-100 text-cyan-800' },
  signing: { label: 'Signing', className: 'bg-indigo-100 text-indigo-800' },
  settling: { label: 'Settling', className: 'bg-purple-100 text-purple-800' },
  completed: { label: 'Completed', className: 'bg-green-100 text-green-800' },
  declined: { label: 'Declined', className: 'bg-orange-100 text-orange-800' },
  expired: { label: 'Expired', className: 'bg-gray-100 text-gray-800' },
  cancelled: { label: 'Cancelled', className: 'bg-red-100 text-red-800' },
};

export function TradeStatusBadge({ status, size = 'sm' }: TradeStatusBadgeProps) {
  const config = STATUS_CONFIG[status] || { label: status, className: 'bg-gray-100 text-gray-800' };

  const sizeClasses = size === 'sm' ? 'px-2 py-1 text-xs' : 'px-3 py-1 text-sm';

  return (
    <span className={`inline-flex rounded-full font-medium ${sizeClasses} ${config.className}`}>
      {config.label}
    </span>
  );
}
